import { cookieName } from '@/context/constants';
import { ToastrOptions, ToastrType } from '@/types/data';
import { Bounce, toast } from 'react-toastify';

export const getBaseUrl = () => {
  if (process.env.NEXT_PUBLIC_APP_URL) {
    return process.env.NEXT_PUBLIC_APP_URL;
  }

  if (
    process.env.VERCEL_ENV === 'production' &&
    process.env.VERCEL_PROJECT_PRODUCTION_URL
  ) {
    return `https://${process.env.VERCEL_PROJECT_PRODUCTION_URL}`;
  }

  if (process.env.VERCEL_URL) {
    return `https://${process.env.VERCEL_URL}`;
  }

  return 'http://localhost:3000';
};

export function getFromLocalStorage(key: string): string | null {
  if (typeof window !== 'undefined') {
    return window.localStorage.getItem(key);
  }
  return null;
}

export function getFromSessionStorage(key: string): string | null {
  if (typeof sessionStorage !== 'undefined') {
    return sessionStorage.getItem(key);
  }
  return null;
}

export function getCookie(name: string) {
  const cookieString = document.cookie;
  const cookies = cookieString.split(';');

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    // Does this cookie string begin with the name we want?
    if (cookie.startsWith(name + '=')) {
      return cookie.substring(name.length + 1);
    }
  }
  return null;
}

export function deleteCookie(name: string) {
  document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
}

type FetchOptions = {
  method?: string;
  headers?: { [key: string]: string };
  body?: string;
};

export async function fetchApi(url: string, options: FetchOptions = {}) {
  try {
    // const BASE_URL =
    //   process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3001/api';
    const token = getCookie(cookieName); // Lấy token từ localStorage

    // console.log('token: ', token);

    const response = await fetch(`/api${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
      },
    });

    if (!response.ok) {
      // Xử lý các mã lỗi HTTP cụ thể (ví dụ: 401, 403)
      if (response.status === 401) {
        // Xử lý khi token hết hạn hoặc không hợp lệ
        // Ví dụ: Chuyển hướng người dùng đến trang đăng nhập
        console.error('Unauthorized: Token may be invalid or expired.');
        // window.location.href = '/login'; // Ví dụ chuyển hướng
        deleteCookie(cookieName);
        window.location.href = `/auth/sign-in?from=${window.location.pathname}`;
        throw new Error('Phiên đăng nhập hết hạn');
      }
      const errorData = await response.json(); //Thêm đoạn này để lấy lỗi chi tiết từ server
      console.log('first response 333: ', errorData);
      throw new Error(
        `${errorData?.exception?.stack?.response?.message || errorData?.exception?.message || 'Unknown error'}`,
      );
    }

    const data = await response.json();
    return data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.log('API Fetch Error:', error.message || error.message);
    throw error; // Ném lại lỗi để component xử lý
  }
}

export function showToastr(message: string, type: ToastrType) {
  const options: ToastrOptions = {
    position: 'bottom-right',
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'light',
    transition: Bounce,
  };

  switch (type) {
    case 'success':
      toast.success(message, options);
      break;
    case 'error':
      toast.error(message, options);
      break;
    case 'info':
      toast.info(message, options);
      break;
    case 'warning':
      toast.warn(message, options);
      break;
    default:
      toast(message, options);
      break;
  }
}

export function getPaginationRange(
  currentPage: number,
  totalPages: number,
  siblingCount = 1,
) {
  // siblingCount: số trang hiển thị hai bên trang hiện tại
  // Ví dụ siblingCount = 1 -> hiển thị: (currentPage - 1), currentPage, (currentPage + 1)
  // Tính tổng số phần tử sẽ hiển thị (bao gồm trang đầu, trang cuối, ...):
  const totalPageNumbers = siblingCount * 2 + 5;
  // 5 ở đây gồm: 1 (trang đầu), totalPages (trang cuối), currentPage, và 2 dấu `...` tiềm năng

  // Nếu tổng số trang không quá số phần tử tối đa -> hiển thị hết
  if (totalPages <= totalPageNumbers) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  // Luôn có trang đầu (1) và trang cuối (totalPages)
  const range = [];

  // Tạo hàm tiện ích để sinh ra dải số liên tục
  const makeRange = (start: number, end: number): number[] => {
    const arr: number[] = [];
    for (let i = start; i <= end; i++) {
      arr.push(i);
    }
    return arr;
  };

  // Xác định vị trí các “vùng” quan trọng
  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

  // Ta sẽ xác định 3 “vùng” hiển thị chính:
  // 1. Vùng đầu (từ 1 đến một số trang nào đó)
  // 2. Vùng giữa (quanh currentPage)
  // 3. Vùng cuối (từ totalPages - x đến totalPages)

  // 1) Thêm trang đầu
  range.push(1);

  // 2) Xử lý hiển thị dấu `...` hoặc các trang tiếp theo sau 1
  // Nếu leftSiblingIndex > 2, nghĩa là có khoảng trống giữa trang 1 và dải trang giữa -> thêm `...`
  if (leftSiblingIndex > 2) {
    range.push('...');
  } else {
    // Nếu không xa quá, hiển thị luôn 2..leftSiblingIndex-1 (nếu muốn)
    const start = 2;
    const end = Math.min(leftSiblingIndex - 1, totalPages - 1);
    range.push(...makeRange(start, end));
  }

  // 3) Thêm dải trang quanh currentPage
  const middleStart = Math.max(leftSiblingIndex, 2);
  const middleEnd = Math.min(rightSiblingIndex, totalPages - 1);
  range.push(...makeRange(middleStart, middleEnd));

  // 4) Xử lý hiển thị dấu `...` hoặc các trang còn lại trước trang cuối
  if (rightSiblingIndex < totalPages - 1) {
    range.push('...');
  } else {
    // Nếu không xa quá, hiển thị các trang kề cuối
    const start = middleEnd + 1;
    const end = totalPages - 1;
    range.push(...makeRange(start, end));
  }

  // 5) Thêm trang cuối
  range.push(totalPages);

  return range;
}
